import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { Box, Container, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import ViewSidebarRoundedIcon from "@mui/icons-material/ViewSidebarRounded";

import BurgerMenu from "./BurgerMenu";
import Menu, { RevbellMenuItem } from "./Menu";
import {
  listMenus,
  MOBILE_QUERY,
  PINNED_WINDOW_WIDTH_MIN_SIZE,
  Row,
} from "../utils";
import { useHasRole } from "../../AuthProvider";
import { menuAtom } from "./SubMenu";
import { setCookie } from "../../services/utils";
import { nancieSidebarOpenAtom } from "../../DataAssistant/atoms";

const NavBar = styled((props: any) => <Box {...props} component="header" />)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  height: "48px",
  overflow: "hidden",
});

const Navigation = () => {
  const intl = useIntl();
  const location = useLocation();
  const [menu, setMenu] = useRecoilState(menuAtom);
  const setNancieSidebarOpen = useSetRecoilState(nancieSidebarOpenAtom);
  const { hasAnyRole } = useHasRole();
  const [display, setDisplay] = useState(false);

  // On /data-assistant routes the Nancie sidebar is an overlay drawer in mobile,
  // toggled from here next to the burger menu (see <NancieSidebar />).
  const isDataAssistant =
    location.pathname === "/data-assistant" ||
    location.pathname.startsWith("/data-assistant/");

  const menuList = useQuery(["menu-items"], () => listMenus(), {
    select: (menus) => {
      return menus.filter((item) => hasAnyRole(item.allowedRoles));
    },
  });

  const showOnlyProfilNav = location.pathname.includes("profil") ? true : false;

  const handleResize = useCallback(() => {
    if (window.innerWidth <= PINNED_WINDOW_WIDTH_MIN_SIZE && menu.pinned) {
      //Update pinned menu to false to restore default value if page is resized with pinned menu
      setMenu((prev) => {
        const newState = {
          ...prev,
          pinned: false,
        };

        setCookie("revbell-menu", JSON.stringify(newState));
        return newState;
      });
    }
    if (window.innerWidth > MOBILE_QUERY) {
      setDisplay(true);
    } else {
      setDisplay(false);
    }
  }, [setMenu, menu.pinned]);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return (
    <Container sx={{ zIndex: 4 }} maxWidth="xl">
      {display ? (
        menuList.data &&
        !showOnlyProfilNav && <Menu menuItems={menuList.data ?? []} />
      ) : (
        <NavBar>
          <Row
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              flex: "1",
              gap: 2,
              maxWidth: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BurgerMenu
                menuItems={menuList.data?.find(
                  (menuItem: RevbellMenuItem) =>
                    menuItem.slug === "dashboard" || menuItem.id === 4
                )}
              />
              {isDataAssistant && (
                <IconButton
                  onClick={() => setNancieSidebarOpen(true)}
                  size="large"
                  sx={{ py: 1.5, px: 0 }}
                  aria-label={intl.formatMessage({
                    id: "IA_NANCIE_TOGGLE_SIDEBAR",
                  })}
                >
                  <ViewSidebarRoundedIcon fontSize="medium" />
                </IconButton>
              )}
            </Box>
          </Row>
        </NavBar>
      )}
    </Container>
  );
};

export default Navigation;
